import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { LogOut, Save, Plus, Trash2, History, Pencil, Trophy, Flag, RotateCcw, Ban, Archive, ArchiveRestore } from "lucide-react";
import { ImageUpload, ImageUploadMulti } from "@/components/ImageUpload";
import { StatusBadge } from "@/components/StatusBadge";
import { ShopAdminTab } from "@/components/shop/ShopAdmin";
import { PopupAdminTab } from "@/components/PopupAdmin";

const ROLE_LABELS = {
  admin: "Full Administrator", scorekeeper: "Scorekeeper", content_editor: "Content Editor",
  vendor_coordinator: "Vendor Coordinator", auction_staff: "Auction Staff",
};
const STATUSES = ["Upcoming", "Happening Now", "Completed", "Delayed", "Canceled"];
const TABS = ["Horses", "Schedule", "Scores", "Fan Favorite", "Shop", "Popup", "Announcements", "Judges", "Sponsors", "Vendors", "Food Trucks", "Map", "FAQs", "Audit"];
const DIVISIONS = ["Adult Undersaddle", "Adult In-Hand", "Youth In-Hand"];

const COLLECTION_CONFIGS = {
  sponsors: {
    path: "sponsors", title: "Sponsors", addDefaults: { name: "", level: "", description: "", website: "", logo: "", social_links: {}, featured: false },
    fields: [
      { key: "name", label: "Business Name", type: "text" },
      { key: "level", label: "Sponsor Level", type: "text" },
      { key: "logo", label: "Logo", type: "image" },
      { key: "website", label: "Website", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "featured", label: "Featured on Home", type: "checkbox" },
    ],
  },
  vendors: {
    path: "vendors", title: "Vendors", addDefaults: { name: "", booth: "", description: "", website: "", logo: "", social_links: {}, special_offer: "" },
    fields: [
      { key: "name", label: "Business Name", type: "text" },
      { key: "booth", label: "Booth / Location", type: "text" },
      { key: "logo", label: "Logo", type: "image" },
      { key: "website", label: "Website", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "special_offer", label: "Special Offer / Giveaway", type: "text" },
    ],
  },
  food_trucks: {
    path: "food_trucks", title: "Food Trucks", addDefaults: { name: "", booth: "", description: "", website: "", logo: "", social_links: {}, special_offer: "" },
    fields: [
      { key: "name", label: "Business Name", type: "text" },
      { key: "booth", label: "Booth / Location", type: "text" },
      { key: "logo", label: "Logo", type: "image" },
      { key: "website", label: "Website", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "special_offer", label: "Special Offer / Giveaway", type: "text" },
    ],
  },
  faqs: {
    path: "faqs", title: "FAQs", addDefaults: { question: "", answer: "", category: "", order: 0 },
    fields: [
      { key: "question", label: "Question", type: "text" },
      { key: "answer", label: "Answer", type: "textarea" },
      { key: "category", label: "Category", type: "text" },
    ],
  },
  map_locations: {
    path: "map_locations", title: "Map Locations", addDefaults: { name: "", category: "Arena", description: "", x: 50, y: 50, order: 0 },
    fields: [
      { key: "name", label: "Location Name", type: "text" },
      { key: "category", label: "Category", type: "select", options: ["Arena", "Parking", "Entrance", "Vendors", "Food", "Restrooms", "First Aid", "Ticketing", "Other"] },
      { key: "description", label: "Description", type: "textarea" },
      { key: "x", label: "Pin X (0–100, left→right)", type: "number" },
      { key: "y", label: "Pin Y (0–100, top→bottom)", type: "number" },
      { key: "order", label: "Sort Order", type: "number" },
    ],
  },
};

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Horses");

  useEffect(() => {
    if (!loading && !user) navigate("/admin", { replace: true });
  }, [loading, user, navigate]);

  if (loading || !user) return <div className="min-h-screen bg-background" />;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 bg-primary px-4 py-3 text-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between">
          <div>
            <p className="font-display text-lg font-bold leading-tight">Admin Dashboard</p>
            <p className="text-xs text-white/60">{user.name} · {ROLE_LABELS[user.role]}</p>
          </div>
          <button data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }}
            className="flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-2 text-sm font-semibold">
            <LogOut size={15} /> Logout
          </button>
        </div>
      </header>

      <div className="sticky top-[60px] z-30 border-b border-border bg-background/90 backdrop-blur">
        <div className="mx-auto flex max-w-3xl gap-1 overflow-x-auto no-scrollbar px-4 py-2">
          {TABS.map((t) => (
            <button key={t} data-testid={`tab-${t.toLowerCase()}`} onClick={() => setTab(t)}
              className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === t ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
              {t}
            </button>
          ))}
        </div>
      </div>

      <main className="mx-auto max-w-3xl px-4 py-4">
        {tab === "Horses" && <HorsesTab />}
        {tab === "Schedule" && <ScheduleTab />}
        {tab === "Scores" && <ScoresTab isAdmin={user.role === "admin"} />}
        {tab === "Fan Favorite" && <FanFavAdminTab />}
        {tab === "Shop" && <ShopAdminTab />}
        {tab === "Popup" && <PopupAdminTab />}
        {tab === "Announcements" && <AnnouncementsTab />}
        {tab === "Judges" && <JudgesTab />}
        {tab === "Sponsors" && <CollectionTab {...COLLECTION_CONFIGS.sponsors} />}
        {tab === "Vendors" && <CollectionTab {...COLLECTION_CONFIGS.vendors} />}
        {tab === "Food Trucks" && <CollectionTab {...COLLECTION_CONFIGS.food_trucks} />}
        {tab === "Map" && <CollectionTab {...COLLECTION_CONFIGS.map_locations} />}
        {tab === "FAQs" && <CollectionTab {...COLLECTION_CONFIGS.faqs} />}
        {tab === "Audit" && <AuditTab isAdmin={user.role === "admin"} />}
      </main>
    </div>
  );
}

const blankHorse = () => ({
  horse_name: "", trainer_name: "", hip_number: null, division: "Adult Undersaddle",
  trainer_state: "", hma: "", horse_age: null, sex: "", height: "", color: "",
  placing: null, auction_status: "In Auction", auction_url: "", video_url: "", lot_number: null, sale_price: null,
  intake_photo: "", current_photos: [], trainer_bio: "", training_story: "",
  training_summary: "", sale_info: "", auction_terms: "", buyer_requirements: "",
  payment_info: "", pickup_info: "", is_scratched: false, is_withdrawn: false, blm_required: false,
});

const errMsg = (e) => {
  const d = e.response?.data?.detail;
  return Array.isArray(d) ? "Enter a valid year" : d || "Something went wrong";
};

function ArchivePanel({ horses, onDone, refreshKey }) {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [years, setYears] = useState([]);
  const [view, setView] = useState("");
  const [archived, setArchived] = useState([]);
  const loadYears = () => api.get("/horses/years").then((r) => setYears(r.data.years || [])).catch(() => {});
  useEffect(() => { loadYears(); }, [refreshKey]);
  useEffect(() => {
    if (!view) { setArchived([]); return; }
    api.get("/horses", { params: { year: view } }).then((r) => setArchived(r.data)).catch(() => {});
  }, [view, refreshKey]);

  const archiveAll = async () => {
    if (!window.confirm(`Archive ALL ${horses.length} current horses under ${year}?\n\nProfiles, placings and sale prices are kept. They move to the ${year} tab on the Mustangs page, lose their Bid Now buttons, and their HIP numbers become free for new horses.`)) return;
    try { const { data } = await api.post("/admin/horses/archive", { year: Number(year), all_current: true }); toast.success(`${data.archived} horses archived to ${data.year}`); loadYears(); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const unarchive = async (h) => {
    try { await api.post(`/admin/horses/${h.id}/unarchive`); toast.success(`${h.horse_name} moved back to current horses`); setArchived(archived.filter((x) => x.id !== h.id)); loadYears(); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-3" data-testid="archive-panel">
      <p className="text-sm font-bold text-primary">Archive by Year</p>
      <p className="mt-0.5 text-xs text-muted-foreground">Archived horses keep their profile, final placing and sale price, and show under their year on the Mustangs page. Use the archive button on a horse to archive just that one.</p>
      <div className="mt-2 flex gap-2">
        <input data-testid="archive-year-input" type="number" value={year} onChange={(e) => setYear(e.target.value)} className="h-11 w-24 rounded-lg border border-border bg-background px-3 font-semibold" />
        <button data-testid="archive-all-btn" onClick={archiveAll} disabled={!horses.length}
          className="h-11 flex-1 rounded-full bg-secondary px-3 text-sm font-bold text-white active:scale-95 disabled:opacity-50">Archive all current horses</button>
      </div>
      {years.length > 0 && (
        <div className="mt-3 border-t border-border pt-3">
          <select data-testid="archived-year-select" value={view} onChange={(e) => setView(e.target.value)} className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm font-semibold">
            <option value="">View archived horses…</option>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          {archived.map((h) => (
            <div key={h.id} data-testid={`archived-horse-${h.hip_number}`} className="mt-2 flex items-center gap-2 text-sm">
              <span className="w-10 font-bold text-primary">#{h.hip_number}</span>
              <span className="min-w-0 flex-1 truncate">{h.horse_name} · {h.division}</span>
              <button data-testid={`unarchive-horse-${h.hip_number}`} onClick={() => unarchive(h)} className="flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs font-semibold text-primary"><ArchiveRestore size={13} /> Unarchive</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HorsesTab() {
  const [horses, setHorses] = useState([]);
  const [editing, setEditing] = useState(null);
  const [auctionDt, setAuctionDt] = useState("");
  const [savingDt, setSavingDt] = useState(false);
  const [archiveTick, setArchiveTick] = useState(0);

  const load = () => api.get("/horses").then((r) => setHorses(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/settings").then((r) => setAuctionDt(r.data?.auction_datetime || "")).catch(() => {}); }, []);

  const saveAuctionDt = async () => {
    setSavingDt(true);
    try { await api.put("/admin/settings", { auction_datetime: auctionDt }); toast.success(auctionDt ? "Auction date saved" : "Auction countdown cleared"); }
    catch { toast.error("Save failed"); }
    setSavingDt(false);
  };

  const save = async () => {
    try {
      if (editing.id) {
        await api.put(`/horses/${editing.id}`, editing);
        toast.success("Horse updated");
      } else {
        await api.post("/horses", editing);
        toast.success("Horse added");
      }
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const remove = async (h) => {
    if (!window.confirm(`Delete HIP #${h.hip_number} — ${h.horse_name}?\n\nThis permanently removes the mustang and its scores. This cannot be undone.`)) return;
    try { await api.delete(`/horses/${h.id}`); toast.success("Mustang deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const archiveOne = async (h) => {
    const y = window.prompt(`Archive HIP #${h.hip_number} — ${h.horse_name} under which year?\n\nIts profile, placing and sale price are kept and it moves to that year's tab.`, String(new Date().getFullYear()));
    if (!y) return;
    try { await api.post("/admin/horses/archive", { year: Number(y), horse_ids: [h.id] }); toast.success(`Archived to ${y}`); load(); setArchiveTick((t) => t + 1); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const F = (label, key, type = "text") => (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</label>
      <input data-testid={`edit-${key}`} type={type} value={editing[key] ?? ""}
        onChange={(e) => setEditing({ ...editing, [key]: type === "number" ? (e.target.value === "" ? null : Number(e.target.value)) : e.target.value })}
        className="mt-1 h-11 w-full rounded-lg border border-border bg-card px-3 outline-none focus:ring-2 focus:ring-secondary" />
    </div>
  );

  if (editing) return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-bold text-primary">{editing.id ? `Edit HIP #${editing.hip_number}` : "Add New Horse"}</h2>
        <button onClick={() => setEditing(null)} className="text-sm font-semibold text-muted-foreground">Cancel</button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {F("Horse Name", "horse_name")}
        {F("Trainer Name", "trainer_name")}
        {F("Hip #", "hip_number", "number")}
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Division</label>
          <select data-testid="edit-division" value={editing.division || ""}
            onChange={(e) => setEditing({ ...editing, division: e.target.value })}
            className="mt-1 h-11 w-full rounded-lg border border-border bg-card px-3 font-semibold outline-none focus:ring-2 focus:ring-secondary">
            {DIVISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        {F("State", "trainer_state")}
        {F("HMA", "hma")}
        {F("Age", "horse_age", "number")}
        {F("Sex", "sex")}
        {F("Height", "height")}
        {F("Color", "color")}
        {F("Placing", "placing", "number")}
        <div>
          <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Auction Status</label>
          <select data-testid="edit-auction_status" value={editing.auction_status || ""}
            onChange={(e) => setEditing({ ...editing, auction_status: e.target.value })}
            className="mt-1 h-11 w-full rounded-lg border border-border bg-card px-3 font-semibold outline-none focus:ring-2 focus:ring-secondary">
            <option value="In Auction">In Auction</option>
            <option value="Not in Auction">Not in Auction</option>
            <option value="Sold">Sold</option>
          </select>
        </div>
        {F("Lot #", "lot_number", "number")}
        {F("Sale Price", "sale_price", "number")}
      </div>
      <div>
        <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Auction Link (Bid Now URL)</label>
        <input data-testid="edit-auction_url" type="url" placeholder="https://auctionsite.com/lot/123"
          value={editing.auction_url ?? ""} onChange={(e) => setEditing({ ...editing, auction_url: e.target.value })}
          className="mt-1 h-11 w-full rounded-lg border border-border bg-card px-3 outline-none focus:ring-2 focus:ring-secondary" />
        <p className="mt-1 text-xs text-muted-foreground">Each horse can link to its own auction page. A "Bid Now" button appears on the horse's page only when Auction Status is "In Auction".</p>
      </div>
      <div>
        <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Video Link (YouTube)</label>
        <input data-testid="edit-video_url" type="url" placeholder="https://youtube.com/watch?v=..."
          value={editing.video_url ?? ""} onChange={(e) => setEditing({ ...editing, video_url: e.target.value })}
          className="mt-1 h-11 w-full rounded-lg border border-border bg-card px-3 outline-none focus:ring-2 focus:ring-secondary" />
        <p className="mt-1 text-xs text-muted-foreground">A "See Video" button appears on the horse's page whenever a link is set.</p>
      </div>
      <ImageUpload label="Intake Photo" testid="edit-intake" value={editing.intake_photo}
        onChange={(v) => setEditing({ ...editing, intake_photo: v })} />
      <ImageUploadMulti label="Current Photos" testid="edit-photos" value={editing.current_photos || []}
        onChange={(v) => setEditing({ ...editing, current_photos: v })} />
      {["trainer_bio", "training_story", "training_summary", "sale_info", "auction_terms", "buyer_requirements", "payment_info", "pickup_info"].map((k) => (
        <div key={k}>
          <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{k.replace(/_/g, " ")}</label>
          <textarea data-testid={`edit-${k}`} value={editing[k] ?? ""} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })}
            rows={k.includes("story") || k.includes("bio") ? 3 : 2}
            className="mt-1 w-full rounded-lg border border-border bg-card p-3 outline-none focus:ring-2 focus:ring-secondary" />
        </div>
      ))}
      <div className="flex gap-3">
        <label className="flex items-center gap-2 text-sm font-semibold">
          <input data-testid="edit-scratched" type="checkbox" checked={editing.is_scratched} onChange={(e) => setEditing({ ...editing, is_scratched: e.target.checked })} /> Scratched
        </label>
        <label className="flex items-center gap-2 text-sm font-semibold">
          <input data-testid="edit-withdrawn" type="checkbox" checked={editing.is_withdrawn} onChange={(e) => setEditing({ ...editing, is_withdrawn: e.target.checked })} /> Withdrawn
        </label>
        <label className="flex items-center gap-2 text-sm font-semibold">
          <input data-testid="edit-blm" type="checkbox" checked={editing.blm_required} onChange={(e) => setEditing({ ...editing, blm_required: e.target.checked })} /> BLM App Required
        </label>
      </div>
      <button data-testid="save-horse" onClick={save}
        className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-primary font-bold text-primary-foreground active:scale-95">
        <Save size={18} /> {editing.id ? "Save Changes" : "Add Horse"}
      </button>
    </div>
  );

  return (
    <div className="space-y-2">
      <div className="rounded-xl border border-secondary/40 bg-secondary/5 p-3" data-testid="auction-countdown-setting">
        <p className="text-sm font-bold text-primary">Auction Countdown</p>
        <p className="mt-0.5 text-xs text-muted-foreground">Set the auction start date &amp; time in <strong>Eastern Time (Kentucky)</strong>. A live countdown to that exact moment shows on the Mustangs page for every visitor. Leave blank to hide it.</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input data-testid="auction-datetime-input" type="datetime-local" value={auctionDt}
            onChange={(e) => setAuctionDt(e.target.value)}
            className="h-11 flex-1 rounded-lg border border-border bg-card px-3 outline-none focus:ring-2 focus:ring-secondary" />
          <button data-testid="save-auction-datetime" onClick={saveAuctionDt} disabled={savingDt}
            className="h-11 rounded-full bg-primary px-4 text-sm font-bold text-primary-foreground active:scale-95 disabled:opacity-50">{savingDt ? "Saving…" : "Save"}</button>
          {auctionDt && (
            <button data-testid="clear-auction-datetime" onClick={() => { setAuctionDt(""); }}
              className="h-11 rounded-full border border-border px-4 text-sm font-semibold text-muted-foreground active:scale-95">Clear</button>
          )}
        </div>
      </div>
      <button data-testid="add-horse" onClick={() => setEditing(blankHorse())}
        className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-primary font-bold text-primary-foreground active:scale-95">
        <Plus size={18} /> Add Horse
      </button>
      <p className="text-sm text-muted-foreground">{horses.length} horses</p>
      <ArchivePanel horses={horses} onDone={load} refreshKey={archiveTick} />
      {horses.map((h) => (
        <div key={h.id} data-testid={`admin-horse-${h.hip_number}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3">
          <span className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10 font-bold text-primary">{h.hip_number}</span>
          <div className="flex-1"><p className="font-semibold text-primary">{h.horse_name}</p>
            <p className="text-xs text-muted-foreground">{h.trainer_name} · {h.division}</p></div>
          <button data-testid={`edit-horse-${h.hip_number}`} onClick={() => setEditing(h)}
            className="grid h-9 w-9 place-items-center rounded-full bg-muted text-primary"><Pencil size={15} /></button>
          <button data-testid={`archive-horse-${h.hip_number}`} onClick={() => archiveOne(h)} title="Archive"
            className="grid h-9 w-9 place-items-center rounded-full border border-secondary/50 bg-secondary/10 text-secondary active:scale-95"><Archive size={15} /></button>
          <button data-testid={`delete-horse-${h.hip_number}`} onClick={() => remove(h)}
            className="grid h-9 w-9 place-items-center rounded-full border border-destructive/40 text-destructive active:scale-95"><Trash2 size={15} /></button>
        </div>
      ))}
    </div>
  );
}

function ScheduleTab() {
  const [items, setItems] = useState([]);
  const blank = { date: "", start_time: "", name: "", arena: "", division: "", description: "", sponsor: "", status: "Upcoming", order: 0 };
  const [nw, setNw] = useState(blank);
  const [adding, setAdding] = useState(false);
  const load = () => api.get("/schedule").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setLocal = (id, changes) =>
    setItems((arr) => arr.map((x) => (x.id === id ? { ...x, ...changes } : x)));

  const save = async (item) => {
    try {
      await api.put(`/schedule/${item.id}`, item);
      toast.success("Schedule saved"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const create = async () => {
    if (!nw.date || !nw.start_time || !nw.name) { toast.error("Date, start time and name are required"); return; }
    setAdding(true);
    try { await api.post("/schedule", nw); toast.success("Class added to schedule"); setNw(blank); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
    setAdding(false);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this schedule item?")) return;
    try { await api.delete(`/schedule/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const cls = "h-10 w-full rounded-lg border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-secondary";

  return (
    <div className="space-y-3">
      <p className="rounded-lg bg-yellow-50 px-3 py-2 text-xs font-medium text-yellow-800">
        Times below are draft/tentative. Edit and save to update the public schedule.
      </p>

      <div data-testid="sched-add-form" className="space-y-2 rounded-xl border-2 border-dashed border-secondary/40 bg-card p-3">
        <p className="text-sm font-bold text-primary">Add class / event</p>
        <div className="grid grid-cols-2 gap-2">
          <input data-testid="sched-new-date" value={nw.date} onChange={(e) => setNw({ ...nw, date: e.target.value })} className={cls} placeholder="Date (e.g. Oct 5)" />
          <input data-testid="sched-new-time" value={nw.start_time} onChange={(e) => setNw({ ...nw, start_time: e.target.value })} className={cls} placeholder="Start time (e.g. 9:00 AM)" />
        </div>
        <input data-testid="sched-new-name" value={nw.name} onChange={(e) => setNw({ ...nw, name: e.target.value })} className={cls} placeholder="Class / event name" />
        <div className="grid grid-cols-2 gap-2">
          <input data-testid="sched-new-arena" value={nw.arena} onChange={(e) => setNw({ ...nw, arena: e.target.value })} className={cls} placeholder="Arena / location" />
          <select data-testid="sched-new-division" value={nw.division} onChange={(e) => setNw({ ...nw, division: e.target.value })} className={cls + " font-semibold"}>
            <option value="">General (no division)</option>
            {DIVISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <textarea data-testid="sched-new-desc" value={nw.description} onChange={(e) => setNw({ ...nw, description: e.target.value })} rows={2} className="w-full rounded-lg border border-border bg-background p-3 text-sm outline-none focus:ring-2 focus:ring-secondary" placeholder="Short description (optional)" />
        <button data-testid="sched-create" onClick={create} disabled={adding} className="flex h-10 w-full items-center justify-center gap-2 rounded-full bg-secondary text-sm font-bold text-secondary-foreground active:scale-95 disabled:opacity-50">
          <Plus size={15} /> Add to schedule
        </button>
      </div>

      {items.map((it) => (
        <div key={it.id} data-testid={`admin-schedule-${it.id}`} className="space-y-2 rounded-xl border border-border bg-card p-3">
          <div className="flex items-center justify-between gap-2">
            <span className="rounded-md bg-muted px-2 py-0.5 text-xs font-bold text-muted-foreground">{it.date}</span>
            <select data-testid={`sched-status-${it.id}`} value={it.status} onChange={(e) => save({ ...it, status: e.target.value })}
              className="h-9 rounded-lg border border-border bg-background px-2 text-sm font-semibold">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <input data-testid={`sched-name-${it.id}`} value={it.name} onChange={(e) => setLocal(it.id, { name: e.target.value })}
            className={cls} placeholder="Event name" />
          <div className="grid grid-cols-2 gap-2">
            <input data-testid={`sched-time-${it.id}`} value={it.start_time} onChange={(e) => setLocal(it.id, { start_time: e.target.value })} className={cls} placeholder="Start time" />
            <input data-testid={`sched-arena-${it.id}`} value={it.arena || ""} onChange={(e) => setLocal(it.id, { arena: e.target.value })} className={cls} placeholder="Arena / location" />
          </div>
          <select data-testid={`sched-division-${it.id}`} value={it.division || ""} onChange={(e) => setLocal(it.id, { division: e.target.value })} className={cls + " font-semibold"}>
            <option value="">General (no division)</option>
            {DIVISIONS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <textarea data-testid={`sched-desc-${it.id}`} value={it.description || ""} onChange={(e) => setLocal(it.id, { description: e.target.value })}
            rows={2} className="w-full rounded-lg border border-border bg-background p-3 text-sm outline-none focus:ring-2 focus:ring-secondary" placeholder="Short description" />
          <input data-testid={`sched-sponsor-${it.id}`} value={it.sponsor || ""} onChange={(e) => setLocal(it.id, { sponsor: e.target.value || null })} className={cls} placeholder="Sponsor (optional)" />
          <div className="flex gap-2">
            <button data-testid={`sched-save-${it.id}`} onClick={() => save(it)}
              className="flex h-10 flex-1 items-center justify-center gap-2 rounded-full bg-primary text-sm font-bold text-primary-foreground active:scale-95">
              <Save size={15} /> Save
            </button>
            <button data-testid={`sched-del-${it.id}`} onClick={() => del(it.id)}
              className="flex h-10 items-center justify-center rounded-full border border-rose-300 bg-rose-50 px-4 text-sm font-bold text-rose-700 active:scale-95">
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function JudgesTab() {
  const [judges, setJudges] = useState([]);
  const load = () => api.get("/judges").then((r) => setJudges(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setLocal = (id, changes) =>
    setJudges((arr) => arr.map((x) => (x.id === id ? { ...x, ...changes } : x)));

  const save = async (j) => {
    try { await api.put(`/judges/${j.id}`, j); toast.success("Judge saved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const add = async () => {
    try { await api.post("/judges", { name: "New Judge", role: "Judge", bio: "", photo: "", order: judges.length }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => { await api.delete(`/judges/${id}`); load(); };

  const cls = "h-10 w-full rounded-lg border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-secondary";

  return (
    <div className="space-y-3">
      <button data-testid="add-judge" onClick={add}
        className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-primary font-bold text-primary-foreground active:scale-95">
        <Plus size={18} /> Add Judge
      </button>
      {judges.map((j) => (
        <div key={j.id} data-testid={`admin-judge-${j.id}`} className="space-y-2 rounded-xl border border-border bg-card p-3">
          <div className="grid grid-cols-2 gap-2">
            <input data-testid={`judge-name-${j.id}`} value={j.name} onChange={(e) => setLocal(j.id, { name: e.target.value })} className={cls} placeholder="Name" />
            <input data-testid={`judge-role-${j.id}`} value={j.role || ""} onChange={(e) => setLocal(j.id, { role: e.target.value })} className={cls} placeholder="Role" />
          </div>
          <ImageUpload label="Photo" testid={`judge-photo-${j.id}`} value={j.photo}
            onChange={(v) => setLocal(j.id, { photo: v })} />
          <textarea data-testid={`judge-bio-${j.id}`} value={j.bio || ""} onChange={(e) => setLocal(j.id, { bio: e.target.value })}
            rows={3} className="w-full rounded-lg border border-border bg-background p-3 text-sm outline-none focus:ring-2 focus:ring-secondary" placeholder="Biography" />
          <div className="flex gap-2">
            <button data-testid={`judge-save-${j.id}`} onClick={() => save(j)}
              className="flex h-10 flex-1 items-center justify-center gap-2 rounded-full bg-primary text-sm font-bold text-primary-foreground active:scale-95"><Save size={15} /> Save</button>
            <button data-testid={`judge-del-${j.id}`} onClick={() => remove(j.id)}
              className="grid h-10 w-10 place-items-center rounded-full border border-border text-destructive"><Trash2 size={16} /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

function AnnouncementsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", message: "", priority: "normal" });
  const load = () => api.get("/announcements").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title || !form.message) return toast.error("Title and message required");
    try {
      await api.post("/announcements", form);
      toast.success("Announcement posted");
      setForm({ title: "", message: "", priority: "normal" }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => { await api.delete(`/announcements/${id}`); load(); };

  return (
    <div className="space-y-4">
      <div className="space-y-2 rounded-xl border border-border bg-card p-4">
        <h3 className="font-display text-lg font-bold text-primary">New Announcement</h3>
        <input data-testid="ann-title" placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
          className="h-11 w-full rounded-lg border border-border bg-background px-3 outline-none focus:ring-2 focus:ring-secondary" />
        <textarea data-testid="ann-message" placeholder="Message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
          className="w-full rounded-lg border border-border bg-background p-3 outline-none focus:ring-2 focus:ring-secondary" />
        <select data-testid="ann-priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}
          className="h-11 w-full rounded-lg border border-border bg-background px-3 font-semibold">
          <option value="normal">Normal Priority</option>
          <option value="high">High Priority (alert banner)</option>
        </select>
        <button data-testid="ann-submit" onClick={create}
          className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-primary font-bold text-primary-foreground active:scale-95">
          <Plus size={18} /> Post Announcement
        </button>
      </div>
      <div className="space-y-2">
        {items.map((a) => (
          <div key={a.id} className="flex items-start gap-3 rounded-xl border border-border bg-card p-3">
            <div className="flex-1">
              <p className="font-semibold text-primary">{a.title} {a.priority === "high" && <span className="text-xs text-destructive">● HIGH</span>}</p>
              <p className="text-sm text-muted-foreground">{a.message}</p>
            </div>
            <button data-testid={`del-ann-${a.id}`} onClick={() => remove(a.id)} className="text-destructive"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function CollectionTab({ path, title, fields, addDefaults }) {
  const [items, setItems] = useState([]);
  const load = () => api.get(`/${path}`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [path]);
  const setLocal = (id, ch) => setItems((a) => a.map((x) => (x.id === id ? { ...x, ...ch } : x)));
  const save = async (it) => {
    try { await api.put(`/${path}/${it.id}`, it); toast.success(`${title} saved`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const add = async () => {
    try { await api.post(`/${path}`, addDefaults); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => { await api.delete(`/${path}/${id}`); load(); };
  const cls = "h-10 w-full rounded-lg border border-border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-secondary";

  return (
    <div className="space-y-3">
      <button data-testid={`add-${path}`} onClick={add}
        className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-primary font-bold text-primary-foreground active:scale-95">
        <Plus size={18} /> Add {title.replace(/s$/, "")}
      </button>
      {items.length === 0 && <p className="text-sm text-muted-foreground">No {title.toLowerCase()} yet. Add entries here as they are confirmed.</p>}
      {items.map((it) => (
        <div key={it.id} data-testid={`admin-${path}-${it.id}`} className="space-y-2 rounded-xl border border-border bg-card p-3">
          {fields.map((f) => (
            f.type === "textarea" ? (
              <textarea key={f.key} data-testid={`${path}-${f.key}-${it.id}`} value={it[f.key] ?? ""} rows={2}
                onChange={(e) => setLocal(it.id, { [f.key]: e.target.value })} placeholder={f.label}
                className="w-full rounded-lg border border-border bg-background p-3 text-sm outline-none focus:ring-2 focus:ring-secondary" />
            ) : f.type === "checkbox" ? (
              <label key={f.key} className="flex items-center gap-2 text-sm font-semibold">
                <input type="checkbox" data-testid={`${path}-${f.key}-${it.id}`} checked={!!it[f.key]}
                  onChange={(e) => setLocal(it.id, { [f.key]: e.target.checked })} /> {f.label}
              </label>
            ) : f.type === "image" ? (
              <ImageUpload key={f.key} label={f.label} testid={`${path}-${f.key}-${it.id}`} value={it[f.key]}
                onChange={(v) => setLocal(it.id, { [f.key]: v })} />
            ) : f.type === "select" ? (
              <div key={f.key}>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-muted-foreground">{f.label}</label>
                <select data-testid={`${path}-${f.key}-${it.id}`} value={it[f.key] ?? ""}
                  onChange={(e) => setLocal(it.id, { [f.key]: e.target.value })} className={cls}>
                  {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            ) : f.type === "number" ? (
              <div key={f.key}>
                <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-muted-foreground">{f.label}</label>
                <input type="number" data-testid={`${path}-${f.key}-${it.id}`} value={it[f.key] ?? ""}
                  onChange={(e) => setLocal(it.id, { [f.key]: e.target.value === "" ? null : Number(e.target.value) })}
                  placeholder={f.label} className={cls} />
              </div>
            ) : (
              <input key={f.key} data-testid={`${path}-${f.key}-${it.id}`} value={it[f.key] ?? ""}
                onChange={(e) => setLocal(it.id, { [f.key]: e.target.value })} placeholder={f.label} className={cls} />
            )
          ))}
          <div className="flex gap-2">
            <button data-testid={`${path}-save-${it.id}`} onClick={() => save(it)}
              className="flex h-10 flex-1 items-center justify-center gap-2 rounded-full bg-primary text-sm font-bold text-primary-foreground active:scale-95"><Save size={15} /> Save</button>
            <button data-testid={`${path}-del-${it.id}`} onClick={() => remove(it.id)}
              className="grid h-10 w-10 place-items-center rounded-full border border-border text-destructive"><Trash2 size={16} /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

const SCORE_CLASSES = [["hc", "Handling & Conditioning"], ["trail", "Trail"], ["freestyle", "Freestyle"]];
const emptyClass = () => ({ judge_scores: [null, null, null, null], bonus_points: 0, bonus_reason: "", penalties: 0, penalty_reason: "" });
const classToEdit = (c) => ({
  judge_scores: ((c?.judges || []).map((j) => j.score).concat([null, null, null, null])).slice(0, 4),
  bonus_points: c?.bonus_points || 0, bonus_reason: c?.bonus_reason || "",
  penalties: c?.penalties || 0, penalty_reason: c?.penalty_reason || "",
});
const classTotal = (c) => c.judge_scores.reduce((a, s) => a + (Number(s) || 0), 0) + (Number(c.bonus_points) || 0) - (Number(c.penalties) || 0);

function ScoresTab({ isAdmin }) {
  const [judges, setJudges] = useState([]);
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(null);
  const [reveal, setReveal] = useState(false);

  const load = () => Promise.all([api.get("/horses"), api.get("/admin/results"), api.get("/judges"), api.get("/settings")]).then(([h, r, j, s]) => {
    setJudges(j.data); setReveal(!!s.data.reveal_judge_scores);
    const byId = Object.fromEntries(r.data.map((x) => [x.horse_id, x]));
    setRows(h.data.map((horse) => {
      const dec = byId[horse.id];
      const ed = dec
        ? { hc: classToEdit(dec.classes.hc), trail: classToEdit(dec.classes.trail), freestyle: classToEdit(dec.classes.freestyle), status: dec.status || "draft", exception: !!dec.exception_reason, exception_reason: dec.exception_reason || "", tie_note: dec.tie_note || "" }
        : { hc: emptyClass(), trail: emptyClass(), freestyle: emptyClass(), status: "draft", exception: false, exception_reason: "", tie_note: "" };
      return { horse, dec, ed };
    }));
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const setClass = (hid, ck, ch) => setRows((rs) => rs.map((r) => r.horse.id === hid ? { ...r, ed: { ...r.ed, [ck]: { ...r.ed[ck], ...ch } } } : r));
  const setEd = (hid, ch) => setRows((rs) => rs.map((r) => r.horse.id === hid ? { ...r, ed: { ...r.ed, ...ch } } : r));
  const setJudge = (hid, ck, idx, val) => setRows((rs) => rs.map((r) => {
    if (r.horse.id !== hid) return r;
    const js = [...r.ed[ck].judge_scores]; js[idx] = val === "" ? null : Number(val);
    return { ...r, ed: { ...r.ed, [ck]: { ...r.ed[ck], judge_scores: js } } };
  }));
  const overall = (ed) => classTotal(ed.hc) + classTotal(ed.trail) + classTotal(ed.freestyle);

  const save = async (r) => {
    const ed = r.ed;
    const body = { hc: ed.hc, trail: ed.trail, freestyle: ed.freestyle, status: ed.status, exception: ed.exception, exception_reason: ed.exception_reason, tie_note: ed.tie_note };
    try { await api.put(`/admin/results/${r.horse.id}`, body); toast.success("Score saved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const del = async (r) => { try { await api.delete(`/admin/results/${r.horse.id}`); toast.success("Score deleted"); setOpen(null); load(); } catch (e) { toast.error("Delete failed"); } };

  const toggleReveal = async () => { const next = !reveal; setReveal(next); try { await api.put("/admin/settings", { reveal_judge_scores: next }); toast.success("Setting updated"); } catch { setReveal(!next); toast.error("Failed"); } };

  const jInput = (r, ck, idx) => (
    <div key={idx}>
      <label className="block truncate text-[10px] font-bold text-muted-foreground">{judges[idx]?.name || `Judge ${idx + 1}`}</label>
      <input data-testid={`score-${ck}-j${idx + 1}-${r.horse.hip_number}`} type="number" min="0" max="100"
        value={r.ed[ck].judge_scores[idx] ?? ""} onChange={(e) => setJudge(r.horse.id, ck, idx, e.target.value)}
        className="h-9 w-full rounded-lg border border-border bg-background px-2 text-sm outline-none focus:ring-2 focus:ring-secondary" />
    </div>
  );

  return (
    <div className="space-y-3">
      <p className="rounded-lg bg-yellow-50 px-3 py-2 text-xs font-medium text-yellow-800">
        Four judges score each class (0–100 each). Class Total = J1 + J2 + J3 + J4 + Bonus − Penalties. Draft & Reviewed stay private; only <strong>Published</strong> or <strong>Final</strong> appear publicly.
      </p>
      {isAdmin && (
        <label className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm font-semibold">
          <input data-testid="reveal-judges-toggle" type="checkbox" checked={reveal} onChange={toggleReveal} />
          Show individual judge scores publicly
        </label>
      )}
      {rows.map((r) => {
        const isOpen = open === r.horse.id;
        return (
          <div key={r.horse.id} data-testid={`admin-score-${r.horse.hip_number}`} className="rounded-xl border border-border bg-card p-3">
            {/* Collapsed summary */}
            <button data-testid={`score-toggle-${r.horse.hip_number}`} onClick={() => setOpen(isOpen ? null : r.horse.id)} className="w-full text-left">
              <div className="flex items-center gap-2">
                <span className="rounded-md bg-primary px-2 py-0.5 text-xs font-bold text-primary-foreground">HIP #{r.horse.hip_number}</span>
                <p className="flex-1 font-semibold text-primary">{r.horse.horse_name}</p>
                <StatusBadge status={{ draft: "Draft", reviewed: "Reviewed", published: "Published", final: "Final" }[r.ed.status]} />
              </div>
              <div className="mt-1.5 flex items-center justify-between text-xs text-muted-foreground">
                <span>{r.horse.division}</span>
                <span>H&C {classTotal(r.ed.hc)} · Trail {classTotal(r.ed.trail)} · FS {classTotal(r.ed.freestyle)}</span>
              </div>
              <div className="mt-0.5 flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Overall (max 1,200)</span>
                <span className="font-display text-xl font-bold text-primary">{overall(r.ed)}{r.dec?.tied ? " (tie)" : ""}</span>
              </div>
            </button>

            {/* Expanded editor */}
            {isOpen && (
              <div className="mt-3 space-y-4 border-t border-border pt-3">
                {SCORE_CLASSES.map(([ck, label]) => (
                  <div key={ck} className="rounded-lg bg-muted/40 p-2.5">
                    <div className="mb-1.5 flex items-center justify-between">
                      <p className="text-xs font-bold uppercase tracking-wider text-secondary">{label}</p>
                      <p className="text-sm font-bold text-primary">Total {classTotal(r.ed[ck])} <span className="text-[10px] font-normal text-muted-foreground">/ 400</span></p>
                    </div>
                    <div className="grid grid-cols-4 gap-2">{[0, 1, 2, 3].map((i) => jInput(r, ck, i))}</div>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-[10px] font-bold text-muted-foreground">Bonus</label>
                        <input data-testid={`score-${ck}-bonus-${r.horse.hip_number}`} type="number" value={r.ed[ck].bonus_points ?? ""} onChange={(e) => setClass(r.horse.id, ck, { bonus_points: e.target.value === "" ? 0 : Number(e.target.value) })} className="h-9 w-full rounded-lg border border-border bg-background px-2 text-sm" />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-muted-foreground">Penalties</label>
                        <input data-testid={`score-${ck}-penalty-${r.horse.hip_number}`} type="number" value={r.ed[ck].penalties ?? ""} onChange={(e) => setClass(r.horse.id, ck, { penalties: e.target.value === "" ? 0 : Number(e.target.value) })} className="h-9 w-full rounded-lg border border-border bg-background px-2 text-sm" />
                      </div>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <input placeholder="Bonus reason" value={r.ed[ck].bonus_reason} onChange={(e) => setClass(r.horse.id, ck, { bonus_reason: e.target.value })} className="h-9 w-full rounded-lg border border-border bg-background px-2 text-xs" />
                      <input placeholder="Penalty reason" value={r.ed[ck].penalty_reason} onChange={(e) => setClass(r.horse.id, ck, { penalty_reason: e.target.value })} className="h-9 w-full rounded-lg border border-border bg-background px-2 text-xs" />
                    </div>
                  </div>
                ))}

                <input placeholder="Tie-break note (if tied)" value={r.ed.tie_note} onChange={(e) => setEd(r.horse.id, { tie_note: e.target.value })} className="h-9 w-full rounded-lg border border-border bg-background px-2 text-sm" />

                {isAdmin && (
                  <label className="flex items-center gap-2 text-xs font-semibold">
                    <input data-testid={`score-exception-${r.horse.hip_number}`} type="checkbox" checked={r.ed.exception} onChange={(e) => setEd(r.horse.id, { exception: e.target.checked })} />
                    Allow status change without all 4 scores (exception)
                  </label>
                )}
                {r.ed.exception && (
                  <input placeholder="Exception reason (required)" value={r.ed.exception_reason} onChange={(e) => setEd(r.horse.id, { exception_reason: e.target.value })} className="h-9 w-full rounded-lg border border-border bg-background px-2 text-xs" />
                )}

                <div className="flex items-center gap-2">
                  <select data-testid={`score-status-${r.horse.hip_number}`} value={r.ed.status} onChange={(e) => setEd(r.horse.id, { status: e.target.value })}
                    className="h-9 flex-1 rounded-lg border border-border bg-background px-2 text-sm font-semibold">
                    <option value="draft">Draft (private)</option>
                    <option value="reviewed">Reviewed (private)</option>
                    <option value="published" disabled={!isAdmin}>{isAdmin ? "Published" : "Published (admin)"}</option>
                    <option value="final" disabled={!isAdmin}>{isAdmin ? "Final" : "Final (admin)"}</option>
                  </select>
                  <button data-testid={`score-save-${r.horse.hip_number}`} onClick={() => save(r)} className="flex h-9 items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-bold text-primary-foreground"><Save size={14} /> Save</button>
                  <button data-testid={`score-delete-${r.horse.hip_number}`} onClick={() => del(r)} className="grid h-9 w-9 place-items-center rounded-full border border-border text-destructive"><Trash2 size={15} /></button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const FAV_LABEL = { Adult: "Adult Undersaddle", InHand: "Adult In-Hand", Youth: "Youth In-Hand" };

function FanFavAdminTab() {
  const [cfg, setCfg] = useState(null);
  const [results, setResults] = useState(null);
  const [votes, setVotes] = useState([]);
  const [hipQ, setHipQ] = useState("");
  const load = () => {
    api.get("/admin/fanfav/config").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/admin/fanfav/results").then((r) => setResults(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const loadVotes = () => api.get("/admin/fanfav/votes", { params: hipQ ? { hip: hipQ } : {} }).then((r) => setVotes(r.data)).catch(() => {});

  const saveCfg = async (ch) => {
    const body = { voting_start: cfg.voting_start || null, voting_end: cfg.voting_end || null,
      manual_override: cfg.manual_override ?? null, adult_open: cfg.adult_open, inhand_open: cfg.inhand_open ?? true, youth_open: cfg.youth_open, ...ch };
    try { await api.put("/admin/fanfav/config", body); toast.success("Voting settings saved"); setCfg({ ...cfg, ...body }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const publish = async (division, horse_id, published) => {
    try { await api.put("/admin/fanfav/publish", { division, horse_id, published }); toast.success(`${FAV_LABEL[division]} winner ${published ? "published" : "unpublished"}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const act = async (id, action) => { try { await api.post(`/admin/fanfav/votes/${id}/${action}`); loadVotes(); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  if (!cfg) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-4">
      <div className="space-y-2 rounded-xl border border-border bg-card p-4">
        <h3 className="font-display text-lg font-bold text-primary">Voting Window</h3>
        <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground">Start</label>
        <input data-testid="fav-start" type="datetime-local" value={(cfg.voting_start || "").slice(0, 16)}
          onChange={(e) => setCfg({ ...cfg, voting_start: e.target.value ? new Date(e.target.value).toISOString() : null })}
          className="h-10 w-full rounded-lg border border-border bg-background px-2 text-sm" />
        <label className="block text-xs font-bold uppercase tracking-wider text-muted-foreground">Close</label>
        <input data-testid="fav-end" type="datetime-local" value={(cfg.voting_end || "").slice(0, 16)}
          onChange={(e) => setCfg({ ...cfg, voting_end: e.target.value ? new Date(e.target.value).toISOString() : null })}
          className="h-10 w-full rounded-lg border border-border bg-background px-2 text-sm" />
        <div className="flex flex-wrap gap-3 pt-1">
          {Object.entries(FAV_LABEL).map(([d, label]) => {
            const k = `${d.toLowerCase()}_open`;
            return <label key={d} className="flex items-center gap-2 text-sm font-semibold"><input data-testid={`fav-${d.toLowerCase()}-open`} type="checkbox" checked={cfg[k] ?? true} onChange={(e) => setCfg({ ...cfg, [k]: e.target.checked })} /> {label} open</label>;
          })}
        </div>
        <div className="flex gap-2 pt-1">
          <button data-testid="fav-save-cfg" onClick={() => saveCfg({})} className="h-10 flex-1 rounded-full bg-primary text-sm font-bold text-primary-foreground">Save Settings</button>
          <button data-testid="fav-open-now" onClick={() => saveCfg({ manual_override: true })} className="h-10 rounded-full border border-border px-3 text-sm font-semibold text-green-700">Open Now</button>
          <button data-testid="fav-close-now" onClick={() => saveCfg({ manual_override: false })} className="h-10 rounded-full border border-border px-3 text-sm font-semibold text-red-700">Close Now</button>
        </div>
      </div>

      {results && (
        <div className="space-y-2 rounded-xl border border-border bg-card p-4">
          <h3 className="font-display text-lg font-bold text-primary">Results</h3>
          <p className="text-sm text-muted-foreground">Verified voters: <strong>{results.verified_voters}</strong></p>
          {Object.keys(FAV_LABEL).filter((div) => results.divisions[div]).map((div) => (
            <div key={div} className="mt-2" data-testid={`fav-results-${div.toLowerCase()}`}>
              <p className="text-xs font-bold uppercase tracking-wider text-secondary">{FAV_LABEL[div]} — {results.divisions[div].total_votes} vote{results.divisions[div].total_votes === 1 ? "" : "s"}</p>
              {results.divisions[div].rankings.length === 0 && <p className="text-xs text-muted-foreground">No votes yet.</p>}
              {results.divisions[div].rankings.map((r, i) => (
                <div key={r.horse_id} className="flex items-center justify-between border-b border-border py-1.5 text-sm">
                  <span>{i + 1}. HIP #{r.hip_number} {r.horse_name} {!r.eligible && <span className="text-xs text-red-600">(ineligible)</span>}</span>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-primary">{r.votes}</span>
                    <button data-testid={`fav-publish-${div.toLowerCase()}-${r.hip_number}`} onClick={() => publish(div, r.horse_id, true)}
                      className="flex items-center gap-1 rounded-full bg-secondary px-2 py-1 text-[11px] font-bold text-white"><Trophy size={11} /> Publish</button>
                  </div>
                </div>
              ))}
              {cfg[`${div.toLowerCase()}_published`] && (
                <button data-testid={`fav-unpublish-${div.toLowerCase()}`} onClick={() => publish(div, cfg[`${div.toLowerCase()}_winner`], false)}
                  className="mt-1 text-xs font-semibold text-red-700 underline">Unpublish {FAV_LABEL[div]} winner</button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="space-y-2 rounded-xl border border-border bg-card p-4">
        <h3 className="font-display text-lg font-bold text-primary">Votes & Moderation</h3>
        <div className="flex gap-2">
          <input data-testid="fav-vote-search" value={hipQ} onChange={(e) => setHipQ(e.target.value)} placeholder="Search by hip #"
            className="h-10 flex-1 rounded-lg border border-border bg-background px-3 text-sm" />
          <button data-testid="fav-vote-search-btn" onClick={loadVotes} className="h-10 rounded-full bg-primary px-4 text-sm font-bold text-primary-foreground">Search</button>
        </div>
        {votes.map((v) => (
          <div key={v.id} className="flex items-center justify-between border-b border-border py-2 text-sm">
            <div>
              <p className="font-semibold text-primary">HIP #{v.hip_number} · {v.division} {v.flagged && <Flag size={12} className="inline text-red-600" />}</p>
              <p className="text-xs text-muted-foreground">{v.voter_masked} · {v.valid ? "valid" : "invalidated"}</p>
            </div>
            <div className="flex gap-1.5">
              <button data-testid={`fav-flag-${v.id}`} onClick={() => act(v.id, "flag")} className="grid h-8 w-8 place-items-center rounded-full border border-border text-yellow-700"><Flag size={13} /></button>
              {v.valid
                ? <button data-testid={`fav-invalidate-${v.id}`} onClick={() => act(v.id, "invalidate")} className="grid h-8 w-8 place-items-center rounded-full border border-border text-red-700"><Ban size={13} /></button>
                : <button data-testid={`fav-restore-${v.id}`} onClick={() => act(v.id, "restore")} className="grid h-8 w-8 place-items-center rounded-full border border-border text-green-700"><RotateCcw size={13} /></button>}
            </div>
          </div>
        ))}
        {votes.length === 0 && <p className="text-xs text-muted-foreground">No votes to show. Search or votes will appear here.</p>}
      </div>
    </div>
  );
}

function AuditTab({ isAdmin }) {
  const [log, setLog] = useState([]);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get("/audit").then((r) => setLog(r.data)).catch(() => setErr(true));
  }, []);
  if (err) return <p className="text-sm text-muted-foreground">Audit history is available to Full Administrators only.</p>;
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-muted-foreground"><History size={16} /><p className="text-sm">Change history</p></div>
      {log.map((l) => (
        <div key={l.id} className="rounded-lg border border-border bg-card p-3 text-sm">
          <p><span className="font-semibold text-primary">{l.user_email}</span> {l.action}d {l.entity} {l.detail && `— ${l.detail}`}</p>
          <p className="text-xs text-muted-foreground">{new Date(l.at).toLocaleString()}</p>
        </div>
      ))}
      {log.length === 0 && <p className="text-sm text-muted-foreground">No changes recorded yet.</p>}
    </div>
  );
}
